import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fanTopics } from "@/mocks/fans";
import { useSeo } from "@/hooks/useSeo";
import Breadcrumb from "@/components/base/Breadcrumb";
import Reveal from "@/components/base/Reveal";

export default function FansPage() {
  useSeo({
    title: "粉丝交流 - 风间由美官方网站",
    description:
      "风间由美粉丝交流专题页，汇集作品讨论、活动招募、音乐听后感与应援话题，和同好一起分享关于她的一切。",
    keywords: "风间由美粉丝,粉丝交流,应援活动,话题讨论,风间由美社区",
  });

  const [openId, setOpenId] = useState<string>("");
  const [toast, setToast] = useState("");

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(""), 2400);
    return () => clearTimeout(timer);
  }, [toast]);

  return (
    <div className="mx-auto w-full max-w-[1320px] px-4 py-10 md:px-6 md:py-14">
      <Breadcrumb items={[{ label: "首页", path: "/" }, { label: "粉丝交流" }]} />

      <header className="mt-6 overflow-hidden rounded-lg border border-background-200 bg-secondary-800">
        <div className="relative px-6 py-10 md:px-10 md:py-14">
          <span className="inline-flex items-center gap-2 rounded-full bg-background-100/15 px-3 py-1 text-xs font-medium text-background-100">
            <i className="ri-chat-smile-2-line" />
            Fans Community
          </span>
          <h1 className="mt-4 font-heading text-3xl font-bold tracking-tight text-background-50 md:text-4xl">
            粉丝交流
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-background-300 md:text-base">
            这里是属于风间由美粉丝的交流空间。我们整理了官方与粉丝共同关注的话题，涵盖作品讨论、活动招募、音乐听后感与应援创意。欢迎在这里留下你的想法，与同好一起分享。
          </p>
        </div>
      </header>

      <div className="mt-10 grid gap-10 lg:grid-cols-12 lg:gap-12">
        <div className="lg:col-span-8">
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            热门话题
          </h2>
          <div className="mt-5 flex flex-col gap-5">
            {fanTopics.map((topic, i) => {
              const open = openId === topic.id;
              return (
                <Reveal key={topic.id} delay={i * 60}>
                  <div className="rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300 md:p-6">
                    <div className="flex flex-wrap items-center gap-3 text-xs">
                      <span className="rounded-full bg-accent-100 px-2.5 py-0.5 font-medium text-accent-900">
                        {topic.tag}
                      </span>
                      <span className="flex items-center gap-1 text-foreground-500">
                        <i className="ri-time-line" />
                        {topic.date}
                      </span>
                      <span className="flex items-center gap-1 text-foreground-500">
                        <i className="ri-chat-3-line" />
                        {topic.replies} 条讨论
                      </span>
                    </div>
                    <h3 className="mt-3 font-heading text-lg font-semibold leading-snug text-foreground-950">
                      {topic.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                      {topic.preview}
                    </p>

                    <div className="mt-4 flex flex-wrap items-center gap-3">
                      <button
                        type="button"
                        onClick={() => setOpenId(open ? "" : topic.id)}
                        className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600"
                      >
                        {open ? "收起话题" : "查看话题"}
                        <i className={open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setToast("演示功能：话题留言与参与功能规划中，敬请期待")}
                        className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                      >
                        <i className="ri-chat-forward-line" />
                        参与讨论
                      </button>
                      <span className="text-xs text-foreground-400">{topic.author} 发起</span>
                    </div>

                    {open ? (
                      <div className="mt-5 space-y-3 rounded-md bg-background-100/70 p-5">
                        {topic.content.map((p, index) => (
                          <p key={index} className="text-sm leading-loose text-foreground-700">
                            {p}
                          </p>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>

        <aside className="lg:col-span-4">
          <div className="flex flex-col gap-6 lg:sticky lg:top-24">
            <div className="rounded-lg border border-background-200 bg-background-100/70 p-5">
              <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-lightbulb-line text-accent-600" />
                推荐理由
              </h2>
              <ul className="mt-4 space-y-3 text-sm leading-relaxed text-foreground-600">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill mt-0.5 text-primary-500" />
                  话题由官方与粉丝共同维护，围绕真实作品与活动展开。
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill mt-0.5 text-primary-500" />
                  留言内容注重友好交流，鼓励分享个人感受与观影体验。
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill mt-0.5 text-primary-500" />
                  优秀内容有机会被选入官方应援影像与精选专题。
                </li>
              </ul>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-links-line text-primary-500" />
                相关专题
              </h2>
              <ul className="mt-4 space-y-2">
                {[
                  { label: "最新影视动态", to: "/news", icon: "ri-film-line" },
                  { label: "作品完整档案", to: "/works", icon: "ri-book-2-line" },
                  { label: "关于风间由美", to: "/about", icon: "ri-user-star-line" },
                  { label: "官方联络方式", to: "/contact", icon: "ri-mail-send-line" },
                ].map((link) => (
                  <li key={link.to}>
                    <Link
                      to={link.to}
                      className="group flex cursor-pointer items-center justify-between rounded-md px-3 py-2.5 transition-colors hover:bg-background-100"
                    >
                      <span className="flex items-center gap-3 text-sm text-foreground-800 transition-colors group-hover:text-primary-600">
                        <i className={`${link.icon} text-base text-primary-500`} />
                        {link.label}
                      </span>
                      <i className="ri-arrow-right-s-line text-foreground-400" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </aside>
      </div>

      {toast ? (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 animate-fade-up rounded-full bg-foreground-950 px-5 py-3 text-sm text-background-50">
          <span className="flex items-center gap-2">
            <i className="ri-information-line text-primary-400" />
            {toast}
          </span>
        </div>
      ) : null}
    </div>
  );
}